/**
 * OCEAN API (Backend/ocean/routes.py). Same contract as For You's client:
 * every call sends ?user_id= and the bearer token; a 401 renews the session
 * once (apiService's single-flight refresh) and asks again; errors come back
 * as the server's sentence.
 */
import { apiService } from '../../services/apiService';
import { featureNotEnabled, FEATURE_NOT_ENABLED_TEXT } from '../../utils/featureAccess';

export type Kind = 'problem' | 'company' | 'solution';

export interface FilterState {
  q: string;
  countries: string[];
  industries: string[];
  goals: string[];
  tags: string[];
}
export const EMPTY_FILTERS: FilterState = { q: '', countries: [], industries: [], goals: [], tags: [] };

export interface SavedRef { collection_id: number; collection: string | null }

export interface SourceInfo {
  type: string | null;
  type_label: string;
  url: string | null;
  date: string | null;
  /** day | month (a date in the URL) · year (content year) · capture (crawl date: a lower bound) */
  precision: 'day' | 'month' | 'year' | 'capture' | null;
  year: number | null;
  age_days: number | null;
}
export interface CompanyMini {
  id: string;
  name: string;
  industry: string | null;
  country: string | null;
  problem_count?: number;
  matching_problems?: number;
  relevance?: number;
  example?: string;
  saved?: SavedRef | null;
}
/** Semantic COSMOS matching state of one statement (Backend/ocean/cosmos_match.py). */
export type CosmosStatus = 'off' | 'none' | 'pending' | 'running' | 'done' | 'empty' | 'failed';
export interface CosmosState { status: CosmosStatus; note: 'cap' | 'retrying' | 'gave_up' | 'refreshing' | null; matched_at: string | null }
/** 'semantic': For You's engine scored them (relevance 0-100, match label);
 *  'keyword': BM25 on shared words, while the statement waits or matching is off. */
export type SolutionsSource = 'semantic' | 'keyword';
export type MatchLabel = 'strong' | 'good';

export interface ProblemItem {
  id: string;
  statement: string;
  tags: string[];
  goals: string[];
  country: string | null;
  industry: string | null;
  company: { id: string; name: string };
  source: SourceInfo;
  source_count: number;
  relevance?: number;
  match?: MatchLabel;
  saved?: SavedRef | null;
}
export interface TechMini {
  id: string;
  title: string;
  university: string | null;
  country: string | null;
  trl: number | null;
  relevance?: number;
  match?: MatchLabel;
}
/** Whether a card's similar / related / COSMOS lists stay within its industry. */
export type Scope = 'industry' | 'all';
/** Admin -> Ocean -> Search engine `card_scope_mode`: 'both' shows the card's
 *  "Matches within" toggle; a single mode forces that scope (no toggle). */
export type ScopeMode = 'both' | 'industry_only' | 'all_only';

export interface ProblemCardData extends Omit<ProblemItem, 'company'> {
  company: CompanyMini;
  confidence: number | null;
  scope: Scope;
  scope_mode?: ScopeMode;
  /** 'semantic': nearest statements by meaning (Backend ocean/vectors.py); 'keyword': shared words. */
  similar_source?: 'semantic' | 'keyword';
  sources: (SourceInfo & { evidence: string | null; confidence: number | null })[];
  related: ProblemItem[];
  similar_companies: CompanyMini[];
  solutions: TechMini[];
  solutions_source: SolutionsSource;
  cosmos: CosmosState;
}
export interface CompanyCardData extends CompanyMini {
  business_model: string | null;
  problems: ProblemItem[];
  similar_companies: CompanyMini[];
  solutions: TechMini[];
  solutions_source: SolutionsSource;
  relevance: number | null;
  relevance_basis: 'problem' | 'search' | null;
  scope: Scope;
  scope_mode?: ScopeMode;
}
export interface SolutionCardData extends TechMini {
  trl_statement: string | null;
  summary: string | null;
  category: string | null;
  subcategories: string | null;
  ip: { summary: string | null; patents: string[] };
  publications: { summary: string | null; items: string[] };
  similar_companies: CompanyMini[];
  related: ProblemItem[];
  cosmos_path: string | null;
  saved?: SavedRef | null;
}

export interface Page<T> { total: number; page: number; per_page: number; pages: number; items: T[] }

export interface Suggestion { count: number; apply: FilterState; scope?: 'filters' | 'keyword' | 'all' }
export interface Suggestions {
  keywords: (Suggestion & { q: string })[];
  goals: (Suggestion & { goal: string })[];
  tags: (Suggestion & { tag: string; goal: string })[];
  relax: (Suggestion & { drop: string; label: string })[];
}
/** The number beside each filter option under the keyword and the OTHER
 *  filters (Backend search.facet_counts); missing name = 0. */
export interface FacetCounts { countries: Record<string, number>; industries: Record<string, number>; goals: Record<string, number> }
/** How the search ran (Backend ocean/engine.py), as COSMOS's search reports it. */
export interface SearchMeta {
  /** keyword | ai: a search with words; filters | browse: none; nonsense: the words did not look like a topic. */
  route: 'keyword' | 'ai' | 'filters' | 'browse' | 'nonsense' | null;
  /** Results held for a search with words (100 keyword, up to 25 AI); filters narrow within them. */
  held: number | null;
  /** Everything the words matched ("100 matches of 1,846 results"). */
  found: number | null;
  capped: boolean;
  score_kind: 'keyword' | 'llm' | null;
  applied_from_query: { dim: 'countries'; value: string; words: string }[];
  corrected?: [string, string][];
  nonsense: { flagged: boolean; reason?: string } | null;
  took_ms?: number;
  cached?: boolean;
}
export interface SearchResult {
  filters: FilterState;
  problems?: Page<ProblemItem>;
  companies?: Page<CompanyMini>;
  suggestions?: Suggestions;
  facets?: FacetCounts;
  search?: SearchMeta;
}
export interface Facet { name: string; problems: number; companies: number }
export interface Meta {
  dataset: { built_at: string; crawl_date: string; problems: number; companies: number; technologies: number };
  goals: { name: string; problems: number; tags: string[] }[];
  countries: Facet[];
  industries: Facet[];
}
/** share_token: set while the collection is shared by link (its owner only sees it). */
export interface Collection { id: number; name: string; is_default: boolean; count: number; share_token?: string | null }
/** A collection someone shared (GET /shared/collections/{token}): read-only. */
export interface SharedCollectionItem { kind: 'problem' | 'company'; id: string; title: string; meta: string }
export interface SharedCollection {
  collection: { name: string; owner_first_name: string | null; count: number; is_owner: boolean; path: string };
  items: SharedCollectionItem[];
}
export interface SavedItem {
  kind: Kind; id: string; collection_id: number; title: string; meta: string; available: boolean; saved_at: string | null;
}
export interface SavedState { collections: Collection[]; items: SavedItem[] }
export interface HistoryEntry {
  id: number; query: string; filters: Partial<FilterState>; hits: number; searched_at: string | null;
}

export interface Res<T> { ok: boolean; data: T | null; status: number; error: string }

export const uid = (): string => { try { return apiService.getStoredUserId() || ''; } catch { return ''; } };
const token = (): string => { try { return apiService.getStoredAccessToken() || ''; } catch { return ''; } };

/** The collection this member last saved into (or picked), per browser: the
 *  next one-click save goes there. A convenience only; a stale id falls back
 *  to the default collection. */
const lastKey = () => `ocean_last_collection:${uid()}`;
export const lastCollectionId = (): number | null => {
  try { const n = Number(localStorage.getItem(lastKey())); return Number.isFinite(n) && n > 0 ? n : null; } catch { return null; }
};
export const rememberCollection = (id: number | null | undefined): void => {
  try { if (id) localStorage.setItem(lastKey(), String(id)); } catch { /* private window */ }
};

const refresh = async (): Promise<boolean> => {
  try {
    const fn = (apiService as any).refreshAccessToken;
    return typeof fn === 'function' ? !!(await fn.call(apiService)) : false;
  } catch { return false; }
};

async function once(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<{ status: number; json: any }> {
  const sep = path.includes('?') ? '&' : '?';
  const url = `${apiService.getApiBaseUrl()}${path}${sep}user_id=${encodeURIComponent(uid())}`;
  const t = token();
  const res = await fetch(url, {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    mode: 'cors',
    credentials: 'omit',
    signal,
  });
  return { status: res.status, json: await res.json().catch(() => ({})) };
}

const errText = (j: any, status: number): string => {
  const d = j?.detail;
  if (typeof d === 'string' && d) return d;
  if (Array.isArray(d)) return 'Please check what you entered.';
  if (status === 401) return 'Please sign in again.';
  if (status === 429) return 'Too many requests — please wait a moment and try again.';
  return `Something went wrong (${status || 'network'}). Please try again.`;
};

async function call<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<Res<T>> {
  if (!uid()) return { ok: false, data: null, status: 401, error: 'Please sign in to use Ocean.' };
  try {
    let r = await once(method, path, body, signal);
    if (r.status === 401 && await refresh()) r = await once(method, path, body, signal);
    if (r.status >= 200 && r.status < 300) return { ok: true, data: r.json as T, status: r.status, error: '' };
    // Feature access (utils/featureAccess.ts) changed under this tab: App re-asks and shows Coming Soon.
    if (featureNotEnabled(r.status, r.json)) return { ok: false, data: null, status: r.status, error: FEATURE_NOT_ENABLED_TEXT };
    return { ok: false, data: null, status: r.status, error: errText(r.json, r.status) };
  } catch (e: any) {
    if (e?.name === 'AbortError') return { ok: false, data: null, status: -1, error: '' };
    return { ok: false, data: null, status: 0, error: 'Network error — check your connection and try again.' };
  }
}

const qs = (f: FilterState, extra: Record<string, string | number>): string => {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  f.countries.forEach(v => p.append('countries', v));
  f.industries.forEach(v => p.append('industries', v));
  f.goals.forEach(v => p.append('goals', v));
  f.tags.forEach(v => p.append('tags', v));
  Object.entries(extra).forEach(([k, v]) => p.set(k, String(v)));
  return p.toString();
};

const B = '/api/v1/ocean';
const enc = encodeURIComponent;
export const oc = {
  meta: () => call<Meta>('GET', `${B}/meta`),
  /** anyway: "Search anyway" after the words were judged not to be a topic. */
  search: (f: FilterState, section: 'all' | 'problems' | 'companies', pPage: number, cPage: number, perPage: number,
           signal?: AbortSignal, opts?: { anyway?: boolean }) =>
    call<SearchResult>('GET', `${B}/search?${qs(f, { section, p_page: pPage, c_page: cPage, per_page: perPage,
                                                     ...(opts?.anyway ? { anyway: 1 } : {}) })}`, undefined, signal),
  /** scope: omitted = the server's default (Admin -> Ocean -> Search engine).
   *  q: the search the card was opened from (steers its related problems). */
  problem: (id: string, scope?: Scope | null, q?: string) => {
    const p = new URLSearchParams();
    if (scope) p.set('scope', scope);
    if (q) p.set('context_q', q.slice(0, 200));
    const s = p.toString();
    return call<{ problem: ProblemCardData }>('GET', `${B}/problems/${enc(id)}${s ? `?${s}` : ''}`);
  },
  /** The card's COSMOS list only; solutions is null while it is still the keyword list. */
  cosmos: (id: string, scope?: Scope | null) =>
    call<{ cosmos: CosmosState; solutions: TechMini[] | null; solutions_source: SolutionsSource }>(
      'GET', `${B}/problems/${enc(id)}/cosmos${scope ? `?scope=${scope}` : ''}`),
  company: (id: string, ctx?: { problem?: string; q?: string }, scope?: Scope | null) => {
    const p = new URLSearchParams();
    if (ctx?.problem) p.set('context_problem', ctx.problem);
    else if (ctx?.q) p.set('context_q', ctx.q);
    if (scope) p.set('scope', scope);
    const s = p.toString();
    return call<{ company: CompanyCardData }>('GET', `${B}/companies/${enc(id)}${s ? `?${s}` : ''}`);
  },
  solution: (id: string) => call<{ solution: SolutionCardData }>('GET', `${B}/solutions/${enc(id)}`),
  saved: () => call<SavedState>('GET', `${B}/saved`),
  save: (kind: Kind, id: string, collectionId?: number) =>
    call<{ collection_id: number; collection: string; created: boolean }>('POST', `${B}/saved`,
      { kind, id, ...(collectionId ? { collection_id: collectionId } : {}) }),
  unsave: (kind: Kind, id: string) => call<{ removed: boolean; collection?: string }>('DELETE', `${B}/saved/${kind}/${enc(id)}`),
  createCollection: (name: string) => call<{ collection: Collection }>('POST', `${B}/collections`, { name }),
  renameCollection: (id: number, name: string) => call<{ collection: Collection }>('PATCH', `${B}/collections/${id}`, { name }),
  deleteCollection: (id: number) => call<{ moved: number }>('DELETE', `${B}/collections/${id}`),
  /** Share a collection by link: its token (issued once, reused) — the share menu makes the tracked link. */
  shareCollection: (id: number) => call<{ share: { token: string; path: string; created: boolean } }>('POST', `${B}/collections/${id}/share`),
  /** Stop sharing: the token and every link made from it stop working. */
  unshareCollection: (id: number) => call<{ revoked: number }>('DELETE', `${B}/collections/${id}/share`),
  sharedCollection: (token: string) => call<SharedCollection>('GET', `${B}/shared/collections/${enc(token)}`),
  history: () => call<{ history: HistoryEntry[] }>('GET', `${B}/history`),
  addHistory: (f: FilterState, hits: number) =>
    call<{ entry: HistoryEntry }>('POST', `${B}/history`, {
      q: f.q, hits, filters: { goals: f.goals, countries: f.countries, industries: f.industries, tags: f.tags },
    }),
  deleteHistory: (id: number) => call<{ deleted: number }>('DELETE', `${B}/history/${id}`),
  clearHistory: () => call<{ deleted: number }>('DELETE', `${B}/history`),
  share: (kind: Kind, id: string) => call<{ path: string }>('POST', `${B}/share`, { kind, id }),
  /** A tracked share link (?s=<code>) brought this person to a card. */
  shareOpen: (code: string, path: string) => call<{ recorded: boolean }>('POST', `${B}/share-open`, { code, path }),
};

/** One recipient of the PRD's Invite form, sent through the app's existing
 *  referral pipeline (/api/v1/invites/referral — it sends the e-mail). */
export interface InviteRecipient { email: string; first_name: string; last_name: string; organisation: string; url: string }
export async function sendInvite(r: InviteRecipient): Promise<{ ok: boolean; exists: boolean; error: string }> {
  const id = uid();
  if (!id) return { ok: false, exists: false, error: 'Please sign in to send invites.' };
  try {
    const res: any = await apiService.sendReferralInvite(id, {
      email: r.email.trim(), first_name: r.first_name.trim(), last_name: r.last_name.trim(), country: '',
      organisation: r.organisation.trim(), designation: '',
      linkedin_url: r.url.trim() ? (/^https?:\/\//i.test(r.url.trim()) ? r.url.trim() : `https://${r.url.trim()}`) : undefined,
    });
    // makeRequest: {success, data: <the route's JSON>} on 2xx, {success: false, error} otherwise.
    if (!res?.success) return { ok: false, exists: false, error: res?.error || 'Could not send.' };
    const d = res.data || {};
    if (d.user_exists) return { ok: false, exists: true, error: '' };
    if (d.success === false) return { ok: false, exists: false, error: d.message || 'Could not send.' };
    return { ok: true, exists: false, error: '' };
  } catch (e: any) {
    return { ok: false, exists: false, error: e?.message || 'Could not send.' };
  }
}
